import { useCallback, useMemo, useState } from 'react';

export interface VersionInfo {
  version: 'v1' | 'v3';
  label: string;
  description: string;
  color: string;
  isV3: boolean;
  canSwitch: boolean;
  switchToVersion: (version: 'v1' | 'v3') => void;
}

const STORAGE_KEY = 'finch-redesign-enabled';

const getStoredVersion = (): 'v1' | 'v3' | null => {
  try {
    return localStorage.getItem(STORAGE_KEY) as 'v1' | 'v3' | null;
  } catch {
    return null;
  }
};

const setStoredVersion = (version: 'v1' | 'v3'): void => {
  try {
    localStorage.setItem(STORAGE_KEY, version);
  } catch {
    // Ignore localStorage errors
  }
};

export const useAppVersion = (): VersionInfo => {
  const [storedVersion, setStoredVersionState] = useState<'v1' | 'v3' | null>(
    getStoredVersion,
  );

  const switchToVersion = useCallback((version: 'v1' | 'v3') => {
    setStoredVersion(version);
    setStoredVersionState(version);

    window.location.reload();
  }, []);

  return useMemo(() => {
    let version: 'v1' | 'v3';
    const canSwitch = true;

    // Priority 1: URL parameter (highest priority - for sharing/testing)
    const urlParams = new URLSearchParams(window.location.search);
    const urlVersion = urlParams.get('version');
    if (urlVersion === 'beta' || urlVersion === 'v3') {
      version = 'v3';
    } else if (urlVersion === 'current' || urlVersion === 'v1') {
      version = 'v1';
    }
    // Priority 2: localStorage (user preference)
    else {
      const stored = getStoredVersion();
      if (stored) {
        version = stored;
      }
      // Priority 3: default to v1
      else {
        version = 'v1';
      }
    }

    const isV3 = version === 'v3';

    const label = isV3 ? 'Beta' : 'Current';
    const description = isV3
      ? 'You are using the new beta interface'
      : 'You are using the current interface';
    const color = isV3
      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';

    return {
      version,
      label,
      description,
      color,
      isV3,
      canSwitch,
      switchToVersion,
    };
  }, [switchToVersion]);
};

/**
 * Hook for conditional logic within components
 *
 * Usage:
 * const isV3 = useIsV3();
 * if (isV3) return <NewUI />;
 * return <CurrentUI />;
 */
export const useIsV3 = () => {
  const { isV3 } = useAppVersion();
  return isV3;
};
